import { Box, Link, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/styles';
import {
  INVITE_FLOW_SCREEN_NAMES,
  REGULAR_FLOW_SCREEN_NAMES,
} from '@/constants/eventTrackingConstants';
import { useEffect, useState } from 'react';
import { push } from '@/utils/matomoUtils';
import { useLazyQuery } from '@apollo/client';
import {
  DuplicateStatus,
  GetDuplicateStatusQuery,
  GetDuplicateStatusQueryVariables,
} from '@/gql/graphql';
import { GET_DUPLICATE_STATUS } from '@/queries';
import LinearLoader from '@/components/LinearLoader';
import { MAX_POLLING_DURATION } from '@/constants';

interface ConfirmationProps {
  sponsorName: string;
  isInviteFlow?: boolean;
  fromDuplicateApp?: boolean;
  applicationId?: string;
}
const Confirmation = ({
  sponsorName,
  isInviteFlow = false,
  fromDuplicateApp = false,
  applicationId,
}: ConfirmationProps) => {
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'));

  const [dupHeader, setDupHeader] = useState('Thank you!');
  const [dupSub1, setDupSub1] = useState(
    `Thanks for using SiteVault Study Connect at the request of ${sponsorName}. Your request is currently under review. Contact your site's SiteVault administrator for immediate access to SiteVault.`
  );
  const [dupSub2, setDupSub2] = useState('');

  const [shouldPoll, setShouldPoll] = useState(fromDuplicateApp);
  const [getDuplicateStatusLoading, setGetDuplicateStatusLoading] = useState(fromDuplicateApp);

  const [getDuplicateStatus] = useLazyQuery<
    GetDuplicateStatusQuery,
    GetDuplicateStatusQueryVariables
  >(GET_DUPLICATE_STATUS, {
    fetchPolicy: 'network-only',
  });

  let timerId;

  useEffect(() => {
    if (isInviteFlow) {
      push(['trackPageView', INVITE_FLOW_SCREEN_NAMES.confirmation]);
    } else {
      push(['trackPageView', REGULAR_FLOW_SCREEN_NAMES.confirmation]);
    }
  }, []);

  const handleGetDupStatus = async () => {
    await getDuplicateStatus({
      variables: { id: applicationId },
      onCompleted: (res) => {
        if (res?.getDuplicateStatus) {
          if (res.getDuplicateStatus === DuplicateStatus.None) {
            return;
          }
          if (timerId) {
            clearInterval(timerId);
          }
          setGetDuplicateStatusLoading(false);

          switch (res.getDuplicateStatus) {
            // Based on duplicateStatus render different copies
            case DuplicateStatus.ExistingUser:
              setDupHeader('Great news! You have a SiteVault account.');
              setDupSub1(`Thanks for using SiteVault at the request of ${sponsorName}.`);
              setDupSub2(
                'Your email address is already associated with an account for your site. Check your email for further instructions.'
              );
              return;
            case DuplicateStatus.DomainMatch:
              setDupHeader('Great news! Your site already uses SiteVault.');
              setDupSub1(`Thanks for using SiteVault at the request of ${sponsorName}.`);
              setDupSub2(
                '1. Check your email for a list of SiteVault administrators at your site\n' +
                  '2. Contact one of your SiteVault administrators to request access'
              );
              return;
            case DuplicateStatus.DomainMismatch:
              setDupHeader('Great news! Your site already uses SiteVault.');
              setDupSub1(
                `Thank you for using SiteVault Study Connect at the request of ${sponsorName}.`
              );
              setDupSub2(
                '1. We’ve sent your contact information to your site’s SiteVault administrator\n' +
                  "2. Contact your site's SiteVault administrator for immediate access to SiteVault"
              );
              return;
            default:
              setDupHeader('Thank you!');
              setDupSub1(
                `Thanks for using SiteVault Study Connect at the request of ${sponsorName}. Your request is currently under review. Contact your site's SiteVault administrator for immediate access to SiteVault.`
              );
              setDupSub2('');
          }
        } else if (timerId) {
          clearInterval(timerId);
        }
      },
    });
  };

  useEffect(() => {
    if (fromDuplicateApp && applicationId && shouldPoll) {
      timerId = setInterval(async () => {
        await handleGetDupStatus();
      }, 1000);
      return () => clearInterval(timerId);
    }
    if (!shouldPoll) {
      setGetDuplicateStatusLoading(false);
    }
    if (timerId) {
      return () => clearInterval(timerId);
    }
    return () => {};
  }, [fromDuplicateApp, shouldPoll]);

  useEffect(() => {
    // Stop polling after 5s
    if (shouldPoll) {
      setTimeout(() => {
        setShouldPoll(false);
      }, MAX_POLLING_DURATION);
    }
  }, [shouldPoll]);

  if (getDuplicateStatusLoading) {
    return <LinearLoader />;
  }

  return (
    <Box>
      <Box
        textAlign="center"
        mt={mdUp ? 9 : 4}
        mb={7}
        sx={{ color: (theme) => theme.palette.grey[800] }}>
        {fromDuplicateApp ? (
          <>
            <Typography variant="h1" sx={{ fontWeight: 500 }} color="inherit">
              {dupHeader}
            </Typography>
            <Typography
              variant="h4"
              sx={{ marginTop: (theme) => theme.spacing(1) }}
              color="inherit">
              {dupSub1}
            </Typography>
            <Typography
              variant="h5"
              sx={{ marginTop: (theme) => theme.spacing(1), whiteSpace: 'pre-wrap' }}
              color="inherit">
              {dupSub2}
            </Typography>
          </>
        ) : (
          <>
            <Typography variant="h1" sx={{ fontWeight: 500 }} color="inherit">
              Thank you!
            </Typography>
            <Typography
              variant="h4"
              sx={{ marginTop: (theme) => theme.spacing(1) }}
              color="inherit">
              Thanks for using SiteVault Study Connect at the request of {sponsorName}
            </Typography>
          </>
        )}
      </Box>

      <Box
        mt={9}
        mb={3}
        p={3}
        sx={{
          borderRadius: 1,
          border: (theme) => `2px solid ${theme.palette.grey[200]}`,
          color: (theme) => theme.palette.grey[800],
        }}>
        <Typography variant="body1" gutterBottom color="inherit" pb={1}>
          Next steps:
        </Typography>

        <ul style={{ paddingLeft: '24px' }}>
          <li style={{ paddingBottom: '16px' }}>
            Please add Veeva to your email address book. Ensuring receipt of Veeva emails is
            critical as they will include important information about your account and onboarding
            process.
          </li>
          <li style={{ paddingBottom: '16px' }}>
            <Link
              target="_blank"
              href={process.env.NEXT_PUBLIC_SITEVAULT_SITE_SUPPORT_URL}
              sx={(theme) => {
                return {
                  color: theme.palette.primary.main,
                  textDecorationColor: theme.palette.primary.main,
                  '&:hover': {
                    color: theme.palette.primary.main,
                    opacity: 0.8,
                  },
                };
              }}>
              Site Support
            </Link>{' '}
            will reach out if they have any additional questions regarding the creation of your new
            SiteVault account.
          </li>
          <li>
            Learn more about{' '}
            <Link
              target="_blank"
              href={process.env.NEXT_PUBLIC_SITEVAULT_STUDY_CONNECT_URL}
              sx={(theme) => {
                return {
                  color: theme.palette.primary.main,
                  textDecorationColor: theme.palette.primary.main,
                  '&:hover': {
                    color: theme.palette.primary.main,
                    opacity: 0.8,
                  },
                };
              }}>
              Study Connect
            </Link>
          </li>
        </ul>
      </Box>
    </Box>
  );
};

export default Confirmation;
